import React, { useState, useEffect, useCallback } from 'react';
import { customFieldsApi } from '../lib/api';
import { PROCUREMENT_SECTORS } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Plus, Trash, Pencil, TextT, Hash, Calendar, ListBullets,
  ToggleLeft, Globe, Envelope, Phone, Lightning, Download
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const ENTITIES = [
  { id: 'contact', label: 'Contacts' },
  { id: 'company', label: 'Companies' },
  { id: 'deal', label: 'Deals' },
  { id: 'procurement', label: 'Procurement' },
  { id: 'task', label: 'Tasks' },
];

const FIELD_TYPES = [
  { id: 'text', label: 'Text', icon: TextT },
  { id: 'number', label: 'Number', icon: Hash },
  { id: 'date', label: 'Date', icon: Calendar },
  { id: 'select', label: 'Select', icon: ListBullets },
  { id: 'multiselect', label: 'Multi Select', icon: ListBullets },
  { id: 'boolean', label: 'Toggle', icon: ToggleLeft },
  { id: 'url', label: 'URL', icon: Globe },
  { id: 'email', label: 'Email', icon: Envelope },
  { id: 'phone', label: 'Phone', icon: Phone },
];

export default function CustomFieldsPage() {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeEntity, setActiveEntity] = useState('deal');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isTemplateOpen, setIsTemplateOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedSector, setSelectedSector] = useState('');
  const [optionInput, setOptionInput] = useState('');
  const [formData, setFormData] = useState({
    name: '', label: '', field_type: 'text', description: '', options: [], required: false, sector: ''
  });

  const fetchFields = useCallback(async () => {
    try {
      const response = await customFieldsApi.getAll({ entity: activeEntity });
      setFields(response.data);
    } catch (error) {
      toast.error('Failed to load custom fields');
    } finally { setLoading(false); }
  }, [activeEntity]);

  useEffect(() => { fetchFields(); }, [fetchFields]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await customFieldsApi.create({ ...formData, entity: activeEntity });
      toast.success('Custom field created');
      setIsDialogOpen(false);
      resetForm();
      fetchFields();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create field');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this custom field?')) return;
    try {
      await customFieldsApi.delete(id);
      toast.success('Field deleted');
      fetchFields();
    } catch (error) { toast.error('Delete failed'); }
  };

  const loadTemplates = async (sector) => {
    setSelectedSector(sector);
    try {
      const response = await customFieldsApi.getTemplates(sector);
      setTemplates(response.data.templates);
      setIsTemplateOpen(true);
    } catch (error) {
      toast.error('Failed to load templates');
    }
  };

  const applyTemplate = async () => {
    try {
      const response = await customFieldsApi.applyTemplate(selectedSector, activeEntity);
      toast.success(`Applied ${response.data.applied} fields from ${selectedSector} template`);
      setIsTemplateOpen(false);
      fetchFields();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply template');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', label: '', field_type: 'text', description: '', options: [], required: false, sector: '' });
    setOptionInput('');
  };

  const getFieldIcon = (type) => {
    const config = FIELD_TYPES.find(f => f.id === type);
    return config?.icon || TextT;
  };

  const entityFields = fields.filter(f => f.entity === activeEntity);

  return (
    <div className="space-y-6" data-testid="custom-fields-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Custom Fields</h1>
          <p className="text-muted-foreground text-sm mt-1">Configure fields per entity & apply sector templates</p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedSector || "pick"} onValueChange={(v) => v !== "pick" && loadTemplates(v)}>
            <SelectTrigger className="w-52" data-testid="sector-template-select">
              <SelectValue placeholder="Apply Sector Template" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pick" disabled>Apply Sector Template</SelectItem>
              {PROCUREMENT_SECTORS.map(s => <SelectItem key={s.id} value={s.id}>{s.label} Template</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="rounded-sm" data-testid="add-field-btn">
            <Plus size={18} className="mr-2" /> Add Field
          </Button>
        </div>
      </div>

      {/* Entity Tabs */}
      <Tabs value={activeEntity} onValueChange={setActiveEntity}>
        <TabsList>
          {ENTITIES.map(e => (
            <TabsTrigger key={e.id} value={e.id} data-testid={`tab-${e.id}`}>{e.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Fields List */}
      {loading ? (
        <div className="text-center p-8 text-muted-foreground">Loading...</div>
      ) : entityFields.length === 0 ? (
        <div className="text-center p-12 border border-dashed border-border rounded-sm">
          <TextT size={40} className="mx-auto text-muted-foreground mb-3 opacity-40" />
          <p className="text-muted-foreground">No custom fields for {ENTITIES.find(e => e.id === activeEntity)?.label}</p>
          <p className="text-xs text-muted-foreground mt-1">Add fields manually or apply a sector template</p>
          <div className="flex gap-2 justify-center mt-4">
            <Button variant="outline" onClick={() => { resetForm(); setIsDialogOpen(true); }}>Add Custom Field</Button>
          </div>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Field</th>
                <th className="text-left p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Type</th>
                <th className="text-left p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Sector</th>
                <th className="text-left p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Required</th>
                <th className="text-left p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Source</th>
                <th className="text-right p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {entityFields.map((field) => {
                const Icon = getFieldIcon(field.field_type);
                return (
                  <tr key={field.id} className="border-b border-border hover:bg-muted/30 transition-colors" data-testid={`field-${field.id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-sm bg-primary/10 flex items-center justify-center">
                          <Icon size={16} className="text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{field.label}</p>
                          <p className="text-xs text-muted-foreground font-mono">{field.name}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge variant="outline" className="text-xs capitalize">{field.field_type}</Badge>
                    </td>
                    <td className="p-4">
                      {field.sector ? (
                        <Badge variant="secondary" className="text-xs">
                          {PROCUREMENT_SECTORS.find(s => s.id === field.sector)?.label || field.sector}
                        </Badge>
                      ) : <span className="text-muted-foreground text-xs">Global</span>}
                    </td>
                    <td className="p-4">
                      {field.required ? <Badge className="bg-amber-500 text-white text-xs">Required</Badge> : <span className="text-xs text-muted-foreground">Optional</span>}
                    </td>
                    <td className="p-4">
                      {field.is_template ? (
                        <Badge variant="outline" className="text-xs"><Lightning size={10} className="mr-1" />Template</Badge>
                      ) : <span className="text-xs text-muted-foreground">Manual</span>}
                    </td>
                    <td className="p-4">
                      <div className="flex justify-end">
                        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleDelete(field.id)} data-testid={`delete-field-${field.id}`}>
                          <Trash size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Options summary for select fields */}
      {entityFields.filter(f => f.field_type === 'select' || f.field_type === 'multiselect').length > 0 && (
        <div className="bg-card border border-border p-4 rounded-sm">
          <h3 className="font-heading font-semibold mb-3">Select Field Options</h3>
          <div className="space-y-2">
            {entityFields.filter(f => f.options?.length > 0).map(f => (
              <div key={f.id} className="flex items-center gap-3">
                <span className="text-sm font-medium w-32">{f.label}:</span>
                <div className="flex flex-wrap gap-1">
                  {f.options.map((opt, i) => <Badge key={i} variant="secondary" className="text-xs">{opt}</Badge>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create Field Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Custom Field to {ENTITIES.find(e => e.id === activeEntity)?.label}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label>Field Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => {
                  const name = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_');
                  setFormData({ ...formData, name, label: formData.label || e.target.value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) });
                }}
                placeholder="e.g. contract_value"
                required
                data-testid="field-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Display Label</Label>
              <Input value={formData.label} onChange={(e) => setFormData({ ...formData, label: e.target.value })} placeholder="e.g. Contract Value" data-testid="field-label" />
            </div>
            <div className="space-y-2">
              <Label>Field Type</Label>
              <Select value={formData.field_type} onValueChange={(v) => setFormData({ ...formData, field_type: v })}>
                <SelectTrigger data-testid="field-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map(ft => <SelectItem key={ft.id} value={ft.id}>{ft.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {(formData.field_type === 'select' || formData.field_type === 'multiselect') && (
              <div className="space-y-2">
                <Label>Options</Label>
                <div className="flex gap-2">
                  <Input value={optionInput} onChange={(e) => setOptionInput(e.target.value)} placeholder="Add option" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (optionInput.trim()) { setFormData({ ...formData, options: [...formData.options, optionInput.trim()] }); setOptionInput(''); } } }} />
                  <Button type="button" variant="outline" onClick={() => { if (optionInput.trim()) { setFormData({ ...formData, options: [...formData.options, optionInput.trim()] }); setOptionInput(''); } }}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-1">{formData.options.map((o, i) => <Badge key={i} variant="secondary" className="cursor-pointer" onClick={() => setFormData({ ...formData, options: formData.options.filter((_, j) => j !== i) })}>{o} x</Badge>)}</div>
              </div>
            )}
            <div className="space-y-2">
              <Label>Description</Label>
              <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Optional description" />
            </div>
            <div className="space-y-2">
              <Label>Sector (optional)</Label>
              <Select value={formData.sector || "none"} onValueChange={(v) => setFormData({ ...formData, sector: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Global (all sectors)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Global (all sectors)</SelectItem>
                  {PROCUREMENT_SECTORS.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={formData.required} onCheckedChange={(v) => setFormData({ ...formData, required: v })} data-testid="field-required" />
              <Label>Required field</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="field-submit">Create Field</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Template Preview Dialog */}
      <Dialog open={isTemplateOpen} onOpenChange={setIsTemplateOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {PROCUREMENT_SECTORS.find(s => s.id === selectedSector)?.label} Template
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              The following fields will be added to <strong>{ENTITIES.find(e => e.id === activeEntity)?.label}</strong>:
            </p>
            {templates.map((tmpl, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-muted/50 rounded-sm">
                <div className="h-8 w-8 rounded-sm bg-primary/10 flex items-center justify-center">
                  {React.createElement(getFieldIcon(tmpl.field_type), { size: 16, className: "text-primary" })}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">{tmpl.label}</p>
                  <p className="text-xs text-muted-foreground">{tmpl.field_type}{tmpl.description ? ` - ${tmpl.description}` : ''}</p>
                </div>
                <Badge variant="outline" className="text-xs capitalize">{tmpl.field_type}</Badge>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsTemplateOpen(false)}>Cancel</Button>
            <Button onClick={applyTemplate} data-testid="apply-template-btn">
              <Download size={14} className="mr-2" /> Apply {templates.length} Fields
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
